<script setup>
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useStore, useStoreGetters } from 'dashboard/composables/store';
import Button from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import PipelineStageForm from '../components/PipelineStageForm.vue';
import PipelineStageList from '../components/PipelineStageList.vue';

const store = useStore();
const getters = useStoreGetters();
const { t } = useI18n();

const selectedProcessId = ref(null);
const processName = ref('');
const teamId = ref(null);
const processSubmitted = ref(false);
const showProcessForm = ref(false);
const showStageForm = ref(false);
const selectedStage = ref(null);
const stagePendingDeletion = ref(null);
const showDeleteConfirmation = ref(false);

const processes = computed(() => getters['conversationPipeline/getProcesses'].value);
const uiFlags = computed(() => getters['conversationPipeline/getUIFlags'].value);
const labels = computed(() => getters['labels/getLabels'].value);
const teams = computed(() => getters['teams/getTeams'].value);
const selectedProcess = computed(() => {
  return processes.value.find(process => process.id === selectedProcessId.value);
});
const stages = computed(() => selectedProcess.value?.stages || []);
const usedLabelIds = computed(() => stages.value.map(stage => stage.label.id));

const selectProcess = process => {
  selectedProcessId.value = process.id;
  processName.value = process.name;
  teamId.value = process.team_id;
  processSubmitted.value = false;
  showProcessForm.value = true;
  showStageForm.value = false;
};

const openNewProcess = () => {
  selectedProcessId.value = null;
  processName.value = '';
  teamId.value = null;
  processSubmitted.value = false;
  showProcessForm.value = true;
  showStageForm.value = false;
};

const saveProcess = async () => {
  processSubmitted.value = true;
  if (!processName.value.trim() || !teamId.value) return;

  try {
    const payload = { name: processName.value.trim(), team_id: teamId.value };
    const process = selectedProcessId.value
      ? await store.dispatch('conversationPipeline/update', {
          id: selectedProcessId.value,
          ...payload,
        })
      : await store.dispatch('conversationPipeline/create', payload);

    selectProcess(process);
    useAlert(t('CRM.PIPELINE.SAVE_SUCCESS'));
  } catch (error) {
    useAlert(error?.response?.data?.message || t('CRM.PIPELINE.SAVE_ERROR'));
  }
};

const openCreateStage = () => {
  selectedStage.value = null;
  showStageForm.value = true;
};

const openEditStage = stage => {
  selectedStage.value = stage;
  showStageForm.value = true;
};

const closeStageForm = () => {
  selectedStage.value = null;
  showStageForm.value = false;
};

const saveStage = async stage => {
  try {
    if (selectedStage.value) {
      await store.dispatch('conversationPipeline/updateStage', {
        processId: selectedProcess.value.id,
        id: selectedStage.value.id,
        position: selectedStage.value.position,
        ...stage,
      });
    } else {
      await store.dispatch('conversationPipeline/createStage', {
        processId: selectedProcess.value.id,
        position: Math.max(0, ...stages.value.map(stageItem => stageItem.position)) + 1,
        ...stage,
      });
    }
    closeStageForm();
    useAlert(t('CRM.PIPELINE.STAGE_SAVE_SUCCESS'));
  } catch (error) {
    useAlert(error?.response?.data?.message || t('CRM.PIPELINE.STAGE_SAVE_ERROR'));
  }
};

const reorderStages = async orderedStages => {
  const stageIds = orderedStages.map(stage => stage.id);
  if (stageIds.every((id, index) => id === stages.value[index]?.id)) return;

  try {
    await store.dispatch('conversationPipeline/reorderStages', {
      processId: selectedProcess.value.id,
      stageIds,
    });
  } catch (error) {
    useAlert(t('CRM.PIPELINE.REORDER_ERROR'));
    store.dispatch('conversationPipeline/get');
  }
};

const confirmDeletion = stage => {
  stagePendingDeletion.value = stage;
  showDeleteConfirmation.value = true;
};

const closeDeleteConfirmation = () => {
  stagePendingDeletion.value = null;
  showDeleteConfirmation.value = false;
};

const deleteStage = async () => {
  try {
    await store.dispatch('conversationPipeline/deleteStage', {
      processId: selectedProcess.value.id,
      id: stagePendingDeletion.value.id,
    });
    closeDeleteConfirmation();
    useAlert(t('CRM.PIPELINE.DELETE_SUCCESS'));
  } catch (error) {
    useAlert(t('CRM.PIPELINE.DELETE_ERROR'));
  }
};

onMounted(async () => {
  try {
    const loadedProcesses = await Promise.all([
      store.dispatch('conversationPipeline/get'),
      store.dispatch('labels/get'),
      store.dispatch('teams/get'),
    ]);
    if (loadedProcesses[0].length) selectProcess(loadedProcesses[0][0]);
  } catch (error) {
    useAlert(t('CRM.PIPELINE.LOAD_ERROR'));
  }
});
</script>

<template>
  <main class="flex flex-col gap-6 p-6 overflow-y-auto">
    <header class="flex flex-col gap-1">
      <h1 class="text-heading-1 text-n-slate-12">{{ $t('CRM.PIPELINE.TITLE') }}</h1>
      <p class="text-n-slate-11">{{ $t('CRM.PIPELINE.DESCRIPTION') }}</p>
    </header>

    <div v-if="uiFlags.isFetching" class="flex items-center gap-2 py-12 justify-center text-n-slate-11">
      <Spinner class="size-5" />
      {{ $t('CRM.PIPELINE.LOADING') }}
    </div>

    <template v-else>
      <section class="grid gap-6 lg:grid-cols-[18rem_minmax(0,1fr)]">
        <aside class="flex flex-col gap-3 p-4 rounded-lg border border-n-weak">
          <div class="flex items-center justify-between gap-3">
            <h2 class="text-heading-2 text-n-slate-12">{{ $t('CRM.PIPELINE.TITLE') }}</h2>
            <Button icon="i-lucide-plus" slate sm @click="openNewProcess" />
          </div>
          <button
            v-for="process in processes"
            :key="process.id"
            type="button"
            class="flex flex-col gap-1 p-3 text-left rounded-lg hover:bg-n-slate-3"
            :class="{ 'bg-n-slate-3': process.id === selectedProcessId }"
            @click="selectProcess(process)"
          >
            <span class="font-medium text-n-slate-12">{{ process.name }}</span>
            <span class="text-sm text-n-slate-11">{{ process.team?.name || $t('CRM.PIPELINE.TEAM_REQUIRED') }}</span>
          </button>
          <p v-if="!processes.length" class="text-sm text-n-slate-11">
            {{ $t('CRM.PIPELINE.SELECT_PROCESS') }}
          </p>
          <Button
            faded
            :label="$t('CRM.PIPELINE.CREATE_PROCESS')"
            @click="openNewProcess"
          />
        </aside>

        <section v-if="showProcessForm" class="flex flex-col gap-4 p-5 rounded-lg border border-n-weak">
          <h2 class="text-heading-2 text-n-slate-12">
            {{ selectedProcessId ? $t('CRM.PIPELINE.DETAILS') : $t('CRM.PIPELINE.NEW_PROCESS') }}
          </h2>
          <woot-input
            v-model="processName"
            :label="$t('CRM.PIPELINE.NAME')"
            :placeholder="$t('CRM.PIPELINE.NAME_PLACEHOLDER')"
            :error="processSubmitted && !processName ? $t('CRM.PIPELINE.NAME_REQUIRED') : ''"
          />
          <label class="flex flex-col gap-1.5 text-sm font-medium text-n-slate-12">
            {{ $t('CRM.PIPELINE.TEAM') }}
            <select
              v-model.number="teamId"
              class="w-full px-3 py-2 rounded-md border bg-n-background border-n-weak text-n-slate-12"
            >
              <option :value="null">{{ $t('CRM.PIPELINE.TEAM_PLACEHOLDER') }}</option>
              <option v-for="team in teams" :key="team.id" :value="team.id">
                {{ team.name }}
              </option>
            </select>
            <span v-if="processSubmitted && !teamId" class="text-sm text-n-ruby-11">
              {{ $t('CRM.PIPELINE.TEAM_REQUIRED') }}
            </span>
          </label>
          <div class="flex justify-end">
            <Button
              :label="$t('CRM.PIPELINE.SAVE')"
              :disabled="uiFlags.isUpdating"
              :is-loading="uiFlags.isUpdating"
              @click="saveProcess"
            />
          </div>
        </section>
      </section>

      <section v-if="selectedProcess?.team_id" class="flex flex-col gap-4">
        <div class="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 class="text-heading-2 text-n-slate-12">{{ $t('CRM.PIPELINE.STAGES') }}</h2>
            <p class="text-sm text-n-slate-11">{{ $t('CRM.PIPELINE.STAGES_DESCRIPTION') }}</p>
          </div>
          <Button
            icon="i-lucide-plus"
            :label="$t('CRM.PIPELINE.ADD_STAGE')"
            :disabled="uiFlags.isUpdating"
            @click="openCreateStage"
          />
        </div>

        <PipelineStageForm
          v-if="showStageForm"
          :stage="selectedStage"
          :labels="labels"
          :used-label-ids="usedLabelIds"
          :is-loading="uiFlags.isUpdating"
          @cancel="closeStageForm"
          @save="saveStage"
        />

        <PipelineStageList
          v-if="stages.length"
          :stages="stages"
          :is-loading="uiFlags.isUpdating"
          @edit="openEditStage"
          @delete="confirmDeletion"
          @reorder="reorderStages"
        />
        <div v-else class="p-8 text-center rounded-lg border border-dashed border-n-weak text-n-slate-11">
          {{ $t('CRM.PIPELINE.NO_STAGES') }}
        </div>
      </section>
    </template>

    <woot-delete-modal
      v-model:show="showDeleteConfirmation"
      :on-close="closeDeleteConfirmation"
      :on-confirm="deleteStage"
      :title="$t('CRM.PIPELINE.DELETE_TITLE')"
      :message="$t('CRM.PIPELINE.DELETE_MESSAGE')"
      :message-value="stagePendingDeletion ? ` ${stagePendingDeletion.name}?` : ''"
      :confirm-text="$t('CRM.PIPELINE.DELETE_CONFIRM')"
      :reject-text="$t('CRM.PIPELINE.CANCEL')"
    />
  </main>
</template>
